import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from 'core/store/authStore';
import { acceptInvitation, validateInvitation } from 'features/invitations/services/invitationApi';
import { ROUTES } from 'core/router/routes';
import type { ValidateInvitationResponse } from 'types/api.generated';

export default function AcceptInvitation() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();
  const { isAuthenticated, hydrateFromMe } = useAuthStore();
  const [preview, setPreview] = useState<ValidateInvitationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Missing invitation token.');
      setLoading(false);
      return;
    }
    validateInvitation(token)
      .then(setPreview)
      .catch(() => setError('Could not validate invitation.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async () => {
    if (!token) return;
    setAccepting(true);
    setError(null);
    try {
      await acceptInvitation({ token });
      await hydrateFromMe();
      navigate(ROUTES.ONBOARDING.SETUP, { replace: true });
    } catch {
      setError('Could not accept invitation. Check you are logged in with the invited email.');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-light">
        <p className="text-slate-500">Validating invitation…</p>
      </div>
    );
  }

  const valid = preview && 'valid' in preview && preview.valid;

  if (!valid) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6">
        <p className="text-slate-700 font-medium">{error ?? 'This invitation is invalid or expired.'}</p>
        <Link to={ROUTES.LOGIN} className="text-primary font-bold">
          Sign in
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-light px-6 py-16 font-display">
      <div className="max-w-md mx-auto bg-white rounded-2xl border border-slate-100 p-8 shadow-sm">
        <h1 className="text-2xl font-black mb-2">Join {preview.organizationName}</h1>
        <p className="text-sm text-slate-600 mb-6">
          {preview.inviterName} invited you as <strong>{preview.roleName}</strong> ({preview.email}).
        </p>
        {!isAuthenticated ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Sign in or create an account with this email to accept.</p>
            <Link
              to={`${ROUTES.LOGIN}?redirect=${encodeURIComponent(`/invite/accept?token=${token}`)}`}
              className="block text-center bg-primary text-white font-bold py-3 rounded-xl"
            >
              Sign in
            </Link>
            <Link
              to={`${ROUTES.SIGNUP}?redirect=${encodeURIComponent(`/invite/accept?token=${token}`)}`}
              className="block text-center border border-slate-200 font-bold py-3 rounded-xl"
            >
              Create account
            </Link>
          </div>
        ) : (
          <button
            type="button"
            disabled={accepting}
            onClick={() => void handleAccept()}
            className="w-full bg-primary text-white font-bold py-3 rounded-xl disabled:opacity-50"
          >
            {accepting ? 'Accepting…' : 'Accept invitation'}
          </button>
        )}
        {error && <p className="text-sm text-red-600 mt-4">{error}</p>}
      </div>
    </div>
  );
}
